import type { Key } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { omitUndefined } from '@/services/utilities/object';

import { VAULTS_TABLE_DEFAULT_LIMIT } from '../constants';
import type { VaultsTableFilters, VaultsTablePagination, VaultsTableSort } from '../types';

export interface VaultsTableStore {
    filters: VaultsTableFilters;
    pagination: Required<VaultsTablePagination>;
    sort: VaultsTableSort;
    selectedRows: Key[];
    setFilters: (filters: VaultsTableFilters) => void;
    setPagination: (pagination: VaultsTablePagination) => void;
    setSort: (sort: VaultsTableSort) => void;
    resetSort: () => void;
    setSelectedRows: (selectedRows: Key[]) => void;
}

const initialState = {
    filters: {},
    pagination: { page: 1, limit: VAULTS_TABLE_DEFAULT_LIMIT },
    sort: {},
    selectedRows: [],
} as const satisfies Partial<VaultsTableStore>;

export const useVaultsTableStore = create<VaultsTableStore>()(
    immer(set => ({
        ...initialState,

        setFilters: filters =>
            set(state => {
                state.filters = omitUndefined(filters);
                // Narrowing the list invalidates the current page, and with it the selection.
                state.pagination.page = 1;
                state.selectedRows = [];
            }),
        setPagination: pagination =>
            set(state => {
                const { page = state.pagination.page, limit = state.pagination.limit } = pagination;

                if (page !== state.pagination.page) {
                    state.selectedRows = [];
                }

                state.pagination = { page, limit };
            }),
        setSort: sort =>
            set(state => {
                state.sort = omitUndefined(sort);
            }),
        resetSort: () =>
            set(state => {
                state.sort = initialState.sort;
            }),
        setSelectedRows: selectedRows =>
            set(state => {
                state.selectedRows = selectedRows;
            }),
    })),
);
